import { gql, useQuery } from '@apollo/client';
import { Spinner, Stack } from '@chakra-ui/react';
import * as React from 'react';

import {
  GetMembersiteEventSponsorshipsResponse,
  MembersiteEventSponsorshipTier,
  getMembersiteEventSponsorships,
} from '@/api/sponsor/eventSponsors';
import { OrganizationProfileImage } from '@/components/OrganizationProfileImage';
import useApi from '@/hooks/useApi';
import { useCurrent } from '@/hooks/useCurrent';
import {
  ADDITIONAL_SPONSORS_BASE_TEMPLATE,
  FEATURED_SPONSOR_BASE_TEMPLATE,
} from '@/modules/events/components/Sponsors/useEventSponsorTiersByEventType';

import {
  SponsorTierList_SponsorsByIds,
  SponsorTierList_SponsorsByIdsVariables,
  SponsorTierList_SponsorsByIds_sponsorsByIds_results,
} from '../../../layouts/components/graphql-types/SponsorTierList_SponsorsByIds';
import { AdditionalSponsorsCard } from '../AdditionalSponsorsCard';
import { PromotionCard } from '../PromotionCard';

const SPONSORS_BY_IDS = gql`
  query SimpleEventSponsorsAside_SponsorsByIds($organizationIds: [String!]!) {
    sponsorsByIds(input: { organizationIds: $organizationIds }) {
      results {
        _id
        name
        ...OrganizationProfileImage_OrganizationFragment
      }
    }
  }
  ${OrganizationProfileImage.organizationFragment}
`;

function isFeaturedSponsorTier(tier: MembersiteEventSponsorshipTier): boolean {
  return tier.baseTemplate === FEATURED_SPONSOR_BASE_TEMPLATE;
}

function isAdditionalSponsorTier(
  tier: MembersiteEventSponsorshipTier
): boolean {
  return tier.baseTemplate === ADDITIONAL_SPONSORS_BASE_TEMPLATE;
}

export const SimpleEventSponsorsAside: React.FC<{
  eventId: string;
}> = ({ eventId }) => {
  const current = useCurrent();
  const tenantId = current.tenant?._id ?? '';

  const { data, loading, execute } =
    useApi<GetMembersiteEventSponsorshipsResponse>(
      getMembersiteEventSponsorships
    );

  React.useEffect(() => {
    if (!tenantId || !eventId) return;

    void execute({ tenantId, eventId }).catch(() => {
      // Keep aside empty on failure
    });
  }, [tenantId, eventId, execute]);

  const tiers = React.useMemo(() => {
    const items = data?.data ?? [];
    return [...items]
      .filter((tier) => tier.organizationIds.length > 0)
      .sort((a, b) => a.displayOrder - b.displayOrder);
  }, [data]);

  const featuredTier = React.useMemo(
    () => tiers.find(isFeaturedSponsorTier) ?? null,
    [tiers]
  );

  const featuredOrganizationId = featuredTier?.organizationIds[0] ?? null;

  const additionalTiers = React.useMemo(
    () => tiers.filter(isAdditionalSponsorTier),
    [tiers]
  );

  const additionalOrganizationIds = React.useMemo(
    () =>
      Array.from(
        new Set(additionalTiers.flatMap((tier) => tier.organizationIds))
      ),
    [additionalTiers]
  );

  const { data: sponsorsByIdsData, loading: sponsorsByIdsLoading } = useQuery<
    SponsorTierList_SponsorsByIds,
    SponsorTierList_SponsorsByIdsVariables
  >(SPONSORS_BY_IDS, {
    variables: { organizationIds: additionalOrganizationIds },
    skip: additionalOrganizationIds.length === 0,
  });

  const organizationsById = React.useMemo(() => {
    const map = new Map<
      string,
      SponsorTierList_SponsorsByIds_sponsorsByIds_results
    >();
    sponsorsByIdsData?.sponsorsByIds.results.forEach((organization) => {
      map.set(organization._id, organization);
    });
    return map;
  }, [sponsorsByIdsData]);

  if (
    (loading && !data) ||
    (sponsorsByIdsLoading &&
      additionalOrganizationIds.length > 0 &&
      !sponsorsByIdsData)
  ) {
    return (
      <Stack align="center" py="4">
        <Spinner size="sm" />
      </Stack>
    );
  }

  if (!featuredOrganizationId && additionalTiers.length === 0) {
    return null;
  }

  return (
    <Stack spacing="4">
      {featuredOrganizationId && featuredTier ? (
        <PromotionCard
          eventId={eventId}
          organizationId={featuredOrganizationId}
          title={featuredTier.name}
        />
      ) : null}
      {additionalTiers.map((tier) => {
        const organizations = tier.organizationIds
          .map((organizationId) => organizationsById.get(organizationId))
          .filter(
            (
              organization
            ): organization is SponsorTierList_SponsorsByIds_sponsorsByIds_results =>
              !!organization
          );

        return (
          <AdditionalSponsorsCard
            key={tier.eventSponsorTierId}
            title={tier.name}
            organizations={organizations}
          />
        );
      })}
    </Stack>
  );
};
